'use client';
import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
} from 'react';

export type TrackableHttpData = {
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: string;
  statusCode?: number;
  statusText?: string;
};

type ChatHttpTrackingContextType = {
  trackHttpRequest: (data: TrackableHttpData) => Promise<void>;
  isTracking: boolean;
};

const ChatHttpTrackingContext = createContext<
  ChatHttpTrackingContextType | undefined
>(undefined);

type ChatHttpTrackingProviderProps = {
  children: ReactNode;
  onTrackRequest?: (data: TrackableHttpData) => Promise<void>;
};

export function ChatHttpTrackingProvider({
  children,
  onTrackRequest,
}: ChatHttpTrackingProviderProps) {
  const [isTracking, setIsTracking] = useState(false);

  const trackHttpRequest = useCallback(
    async (data: TrackableHttpData) => {
      if (!onTrackRequest) return;
      setIsTracking(true);
      try {
        await onTrackRequest(data);
      } finally {
        setIsTracking(false);
      }
    },
    [onTrackRequest],
  );

  const contextValue: ChatHttpTrackingContextType = useMemo(
    () => ({
      trackHttpRequest,
      isTracking,
    }),
    [trackHttpRequest, isTracking],
  );

  return (
    <ChatHttpTrackingContext.Provider value={contextValue}>
      {children}
    </ChatHttpTrackingContext.Provider>
  );
}

export function useChatHttpTracking() {
  const context = useContext(ChatHttpTrackingContext);
  // Return undefined instead of throwing - tracking is optional
  return context;
}
